/* eslint-disable spire/fenced-imports */
/* eslint-disable camelcase */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import sleep from "@insite/client-framework/Common/Sleep";
import { addSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { PositionStyle } from "@NA/Components/ADIHeaderSearchPopover";
import useCoveoSearch from "@NA/Components/Coveo/Commerce/useCoveoSearch";
import { HasCoveoHooks, withCoveoHooks } from "@NA/Components/Coveo/Contexts/CoveoHooksContext";
import CoveoDealerCertModal from "@NA/Components/Coveo/CoveoDealerCertModal";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import toggleRewardsDrawer from "@NA/Handlers/ADIRewardsDrawerHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { CoveoActions } from "@NA/Store/Reducer/CoveoCommerceReducer";
import { CoveoProductModel } from "@NA/Types/CoveoModel";
import ADIColors from "@NA/Utils/ADIColors";
import CoveoSearchPopover from "@NA/Widgets/Coveo/Header/CoveoSearchPopover";
import React, { FocusEvent, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import debounce from "lodash/debounce";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    extendedStyles?: SearchInputStyles;
    shellContext?: any;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit },
    } = getSettingsCollection(state);

    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        toggleDrawer: state.ADIGlobalData.loginDrawerStore.loginDrawer,
        isAuthenticated: state.context.session.isAuthenticated,
        coveoCommerce: state.ADIGlobalData.coveoCommerce,
    };
};

const mapDispatchToProps = {
    toggleLoginDrawer,
    toggleRewardsDrawer,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasCoveoHooks &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                padding: 6px;
                transform: rotate(90deg);
                svg {
                    height: 24px;
                    width: 24px;
                    color: ${ADIColors.darkBlue1};
                    stroke-width: 2.5px;
                }
            `,
        },
        border: "rounded",
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 3px;
                font-size: 12px;
                width: 100%;
                float: right;
                height: 34px;
                padding: 9px 40px 9px 15px;
                font-weight: 600;
                border: 1px solid ${ADIColors.gray3};

                :focus-within {
                    padding: 9px 40px 9px 15px;
                    outline-color: ${ADIColors.darkBlack3};
                    outline-style: none;
                    outline-width: 0px;
                }
                &:focus {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                null,
                                css`
                                    outline-color: ${ADIColors.darkBlack3};
                                    outline-style: none;
                                    outline-width: 0px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
                &:focus-visible {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                null,
                                css`
                                    outline-color: ${ADIColors.darkBlack3};
                                    outline-style: ${getProp("theme.focus.style", "solid")};
                                    outline-width: 1px;
                                    outline-offset: 1px;
                                `,
                            ],
                            "min",
                        );
                    }}
                }
                ::placeholder {
                    color: ${ADIColors.gray3};
                    opacity: 1;
                }

                ::-ms-input-placeholder {
                    color: ${ADIColors.gray3};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 400px;
                        `,
                    ])}
            `,
            formInputWrapper: css`
                display: flex;
                > button {
                    width: 35px !important;
                    height: 35px !important;
                    padding: 4px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                right: 0px !important;
                            `,
                            css`
                                right: 0px !important;
                            `,
                            null,
                        ])}
                    > ${IconWrapper} {
                        top: 0px;
                        right: 0px;
                        width: 100%;
                        height: 100%;
                        padding: 4px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 338.63px;
                        `,
                    ])}
                @media only screen and (max-width: 1700px) {
                    max-width: 400px;
                }
                @media only screen and (min-width: 1700px) {
                    max-width: 400px;
                }
            `,
        },
    },
};

const SearchInputWrapper = styled.div<any>`
    display: flex;
    align-items: center;
    position: ${({ showSearchBar }) => (showSearchBar ? "fixed" : "relative")};
    width: ${({ showSearchBar }) => (showSearchBar ? "100%" : "auto")};
    //left: ${({ showSearchBar }) => (showSearchBar ? "0px" : "auto")};
    //right: ${({ showSearchBar }) => (showSearchBar ? "15px" : "0")};
    //bottom: ${({ showSearchBar }) => (showSearchBar ? "0px" : "5px")};
     left: 0px;
    right: 15px;
    //bottom: 15px;
    padding: ${({ showSearchBar }) => (showSearchBar ? "15px" : "0px")};
    top: ${({ showSearchBar, isbanner, isSafari, topBannerHeight }) => {
        if (showSearchBar) {
            if (isSafari) {
                return topBannerHeight;
            }
            if (isbanner === null) {
                return "30px";
            }
            if (isbanner === "close") {
                return "31px";
            }
        }
        return "0px!important";
    }};
    height: ${({ showSearchBar }) => (showSearchBar ? "61px" : "auto")};
    z-index: ${({ showSearchBar }) => (showSearchBar ? "1200" : "0")};
    gap: ${({ showSearchBar }) => (showSearchBar ? "10px" : "0px")};
    background-color: ${ADIColors.white};
`;

const CoveoSearchInput = ({
    extendedStyles,
    toggleDrawer,
    searchHistoryEnabled,
    searchHistoryLimit,
    autocompletePositionFunction,
    coveoCommerce,
    toggleLoginDrawer,
    toggleRewardsDrawer,
    searchBox,
    instantProducts,
}: Props) => {
    const dispatch = useDispatch();
    const { searchInputRef, doCoveoSearch } = useCoveoSearch();
    const { state: searchBoxState, methods: searchBoxMethods } = searchBox;
    const { methods: instantProductsMethods } = instantProducts;
    const [query, setSearchQuery] = useState("");
    const [topBannerHeight, setTopBannerHeight] = useState("0px");

    useEffect(() => {
        const getTopBannerHeight = Array.from(document.getElementsByTagName("div"))?.filter(e =>
            e?.className?.startsWith("TextSliderWrapper"),
        )[0]?.clientHeight;
        setTopBannerHeight(`${getTopBannerHeight || 0}px`);
    }, []);

    const isSafari = IS_SERVER_SIDE
        ? false
        : /(\bChrome\b|\bSafari\b|\bMozilla\b)\//.exec(navigator?.userAgent)![1].toLocaleLowerCase() === "safari";

    const [showSearchBar, setShowSearchBar] = useState(false);
    const [isBanner, setIsBanner] = useState<string | null>(null);
    const popoverRef = useRef<HTMLUListElement>(null);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 767);

    useEffect(() => {
        const sliderResize = () => {
            setIsMobile(window.innerWidth < 767);
        };
        window.addEventListener("resize", sliderResize);
        return () => {
            window.removeEventListener("resize", sliderResize);
        };
    }, []);

    useEffect(() => {
        if (showSearchBar && isMobile) {
            dispatch({
                type: "HomePage/IsSeacrhBarOpen",
            });
        } else {
            dispatch({
                type: "HomePage/IsSeacrhBarClose",
            });
        }
    }, [showSearchBar, isMobile]);

    const clearSearch = () => {
        setSearchQuery("");
    }

    const closeAutocomplete = (e?: FocusEvent<HTMLInputElement>) => {
        document.documentElement.style.overflow = "";
    };

    const openAutocomplete = () => {
        if (toggleDrawer) {
            toggleLoginDrawer({ loginDrawer: false });
            toggleRewardsDrawer({ rewardsDrawer: false });
        }
        (popoverRef.current as any)?.openPopover();
        document.documentElement.style.overflowY = "hidden";
        const isMobileOrTablet = window.innerWidth <= 768;
        if (isMobileOrTablet) {
            document.body.style.top = "0px";
        }
        setTimeout(() => {
            const searchInputPopover = document.getElementById("headerSearch-popover");
            const searchInputPopoverOverlay = document.getElementById("overlay-div");
            const childDivs = searchInputPopover?.getElementsByTagName("div");
            const headerElement = document.querySelector('[data-test-selector="header"]');
            const hasChildren = childDivs && childDivs.length > 1;

            if (searchInputPopover && searchInputPopoverOverlay) {
                searchInputPopover.style.display = hasChildren ? "flex" : "none";
                searchInputPopoverOverlay.style.display = hasChildren ? "flex" : "none";
            }
            if (headerElement && searchInputPopover) {
                const headerHeight = headerElement.getBoundingClientRect().height;
                const isMobileOrTablet = window.innerWidth <= 768;
                if (isMobileOrTablet) {
                    searchInputPopover.style.top = `${headerHeight}px`;
                }
            }
        }, 100);
    };

    const [dealerModalIsOpen, setDealerModalIsOpen] = React.useState(false);
    const [coveoProduct, setCoveoProduct] = React.useState<CoveoProductModel>();
    const openDealerModal = (coveoProduct: CoveoProductModel) => {
        (popoverRef.current as any)?.closePopover();
        setCoveoProduct(coveoProduct);
        setDealerModalIsOpen(true);
    };

    const handleDealerModalClose = async () => {
        setDealerModalIsOpen(false);
        // wait for modal to close
        await sleep(500);
        openAutocomplete();
    };

    const loadAutocomplete = debounce((query: string) => {
        searchBoxMethods?.updateText(query);
        instantProductsMethods?.updateQuery(query);
    }, 200);
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const isPopoverOpen = (popoverRef.current as any)?.state?.open;
    
        let hasSearchHistory = false;
        if (value.length === 0) {
            hasSearchHistory = getSearchHistory().length > 0;
        }
    
        const shouldOpen = value.length > 1 || (value.length === 0 && hasSearchHistory);
        const shouldClose = isPopoverOpen && (value.length === 1 || (value.length === 0 && !hasSearchHistory));
    
        if (shouldOpen) {
            openAutocomplete();
        } else if (shouldClose) {
            closeAutocomplete();
            (popoverRef?.current as any)?.closePopover?.();
        }
    
        setSearchQuery(value);
    
        if (value.length >= 2) {
            loadAutocomplete(value);
        }
    };

    const styles = mergeToNew(searchInputStyles, extendedStyles);
    const searchId = `headerSearch`;

    const closeSignInDrawer = () => {
        if (toggleDrawer) {
            toggleLoginDrawer({ loginDrawer: false });
        }
    };

    const doSearch = () => {
        if (query.trim() === "") {
            return;
        }
        if (searchHistoryEnabled) {
            addSearchHistory(query, searchHistoryLimit);
        }
        doCoveoSearch(query);
        clearSearch();
        closeAutocomplete();
        window.scrollTo(0, 0);
        setShowSearchBar(!showSearchBar);
        (popoverRef?.current as any)?.closePopover();
    };

    const handleSearchBar = () => {
        closeAutocomplete();
        const isAppBanner = localStorage.getItem("bannerclosebutton");
        setIsBanner(isAppBanner);
        setShowSearchBar(!showSearchBar);
        (popoverRef?.current as any)?.closePopover();
    };

    const handleInteraction = () => {
        window.scrollTo(0, 0);
        query && loadAutocomplete(query);
        ((getSearchHistory().length > 0 && query.length === 0) || query.length >= 2) && openAutocomplete();
    };

    const TextInput = (
        <TextField
            {...styles.input}
            ref={searchInputRef}
            value={query}
            iconClickableProps={{ onClick: () => doSearch() }}
            onChange={e => handleInputChange(e)}
            onKeyPress={e => e.key === "Enter" && doSearch()}
            placeholder={translate("Search by product, brand or keyword...")}
            autoComplete="off"
            clickableText={translate("submit search")}
            id={searchId}
            data-test-selector="headerSearchInputTextField"
            onClick={handleInteraction}
        />
    );

    const searchInput = (
        <>
            <Hidden below="md" style={{ width: "100%" }}>
                {TextInput}
            </Hidden>
            <Hidden above="sm">
                <SearchInputWrapper
                    showSearchBar={showSearchBar}
                    isbanner={isBanner}
                    isSafari={isSafari}
                    topBannerHeight={topBannerHeight}
                >
                    {showSearchBar && TextInput}
                    <ButtonIcon
                        color={ADIColors.darkBlue1}
                        size={showSearchBar ? 35 : 30}
                        src={showSearchBar ? X : Search}
                        onClick={() => handleSearchBar()}
                    />
                </SearchInputWrapper>
            </Hidden>
        </>
    );

    return (
        <>
            <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                {translate("Site Search")}
            </VisuallyHidden>
            <CoveoSearchPopover
                popoverRef={popoverRef}
                searchId={searchId}
                searchInput={searchInput}
                searchInputStyles={styles}
                closeAutocomplete={closeAutocomplete}
                openDealerModal={openDealerModal}
                closeSignInDrawer={closeSignInDrawer}
                query={query}
                clearSearch={clearSearch}
                autocompletePositionFunction={autocompletePositionFunction}
                onClose={!dealerModalIsOpen ? closeAutocomplete : () => {}}
            />
            <CoveoDealerCertModal
                open={dealerModalIsOpen}
                coveoProduct={coveoProduct}
                onClose={handleDealerModalClose}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCoveoHooks(withHistory(withTheme(CoveoSearchInput))));
